import api from './api';
import type { InventoryBalance } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/balance`;

export interface InventoryBalanceLocationEntry {
  locationId?: string;
  quantity: number;
  lastCost?: number | null;
  lastMovementType?: string;
  lastUpdated: string;
}

/**
 * Venue-wide aggregate returned by GET /balance/:inventoryItemId (no locationId param).
 * This is NOT an InventoryBalance document — it is computed on the fly by the backend.
 */
export interface InventoryBalanceAggregate {
  inventoryItemId: string;
  venueId: string;
  totalQuantity: number;
  locationBreakdown: InventoryBalanceLocationEntry[];
}

const inventoryBalanceService = {
  /**
   * Get venue-wide balance aggregate for a single inventory item.
   * Returns totalQuantity (sum across all locations) and per-location breakdown.
   * Use this for the item detail page.
   */
  getItemBalances: async (
    venueId: string,
    inventoryItemId: string,
  ): Promise<InventoryBalanceAggregate> => {
    const response = await api.get(`${base(venueId)}/${inventoryItemId}`);
    return response.data;
  },

  /**
   * Get current balance for a single inventory item at a specific location.
   * InventoryBalance is a derived read model — never written directly.
   * lastCost is display/cache only; real cost history is in InventoryMovement.
   */
  getBalance: async (
    venueId: string,
    inventoryItemId: string,
    locationId?: string,
  ): Promise<InventoryBalance> => {
    const params: Record<string, unknown> = {};
    if (locationId) params.locationId = locationId;
    const response = await api.get(`${base(venueId)}/${inventoryItemId}`, { params });
    return response.data;
  },

  /**
   * Get balances for all inventory items at a venue, optionally filtered by location.
   * Typically returned as a map keyed by inventoryItemId for O(1) lookups in the UI.
   */
  getBalances: async (
    venueId: string,
    locationId?: string
  ): Promise<Record<string, InventoryBalance>> => {
    const params: Record<string, unknown> = {};
    if (locationId) params.locationId = locationId;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /**
   * Recompute the cached balance for one (item, location) pair by summing its movement ledger.
   * Does NOT modify movements. Returns before/after/difference.
   * locationId is required — the backend aggregates only movements matching the exact pair.
   */
  recomputeBalance: async (
    venueId: string,
    itemId: string,
    locationId: string,
  ): Promise<{ before: number; after: number; difference: number }> => {
    const response = await api.post(
      `/admin/venue/${venueId}/inventory/item/${itemId}/recompute-balance`,
      { locationId },
    );
    return response.data;
  },
};

export default inventoryBalanceService;
